import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { signalsData } from "Stores/signalsData";
import { agentsData } from "Stores/agentsData";
import SignalIcon from "./SignalIcon";
import SignalPicker from "./SignalPicker";

const TIMINGS = [{ value: "INSTANT", label: "Instantly" }, { value: "HOURLY", label: "Hourly digest" }, { value: "DAILY", label: "Daily digest" }];
const hourLabel = h => (h === 0 || h === 24 ? "12 am" : h < 12 ? `${h} am` : h === 12 ? "12 pm" : `${h - 12} pm`);
const HOURS = Array.from({ length: 25 }, (_, h) => ({ value: h, label: hourLabel(h) }));

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return "UTC";
  }
};

/** A tidy list of zones: the browser's own first, then the full list when the browser can give one. */
const zoneOptions = current => {
  let all = [];
  try {
    all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  } catch (e) {
    all = [];
  }
  const own = [browserZone(), current, "UTC"].filter(Boolean);
  return Array.from(new Set([...own, ...all]));
};

const PRIORITIES = [["hot", "Hot signals"], ["warm", "Warm signals"]];

/** How the member hears about signals outside the app: per priority timing and channels, plus active hours. */
const SignalNotificationSettings = ({ onClose }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    signalsData.fetchNotificationSettings().then(s => {
      if (s) setForm({ ...s, hot: { ...s.hot }, warm: { ...s.warm }, slackChannelId: s.slackChannelId || "", timezone: s.timezone && s.timezone !== "UTC" ? s.timezone : browserZone() });
    });
    agentsData.fetchSlackChannels && agentsData.fetchSlackChannels();
  }, []);

  if (!form) return null;

  const setRule = (key, patch) => setForm({ ...form, [key]: { ...form[key], ...patch } });
  const anySlack = form.hot.slack || form.warm.slack;
  const channels = agentsData.slackChannels || [];

  const save = async () => {
    setBusy(true);
    setError("");
    const result = await signalsData.saveNotificationSettings({
      hot: form.hot,
      warm: form.warm,
      slackChannelId: form.slackChannelId,
      activeHoursEnabled: form.activeHoursEnabled,
      activeStartHour: Number(form.activeStartHour),
      activeEndHour: Number(form.activeEndHour),
      weekdaysOnly: form.weekdaysOnly,
      timezone: form.timezone
    });
    setBusy(false);
    if (result.error) setError(result.error);
    else onClose();
  };

  // Portalled to the body so the backdrop covers the right rail too (.mainContent is a containing block).
  return ReactDOM.createPortal(
    <div className="sg-drawer-bg" onClick={onClose}>
      <div className="sg-drawer" onClick={e => e.stopPropagation()}>
        <div className="sg-drawer__head">
          <span className="sg-tile sg-tile--lg eng"><SignalIcon id="bell" size={22} /></span>
          <div className="sg-drawer__title">
            <h2>Signal notifications</h2>
            <p>The badge and your Signals feed are always instant. Choose what else you want.</p>
          </div>
          <button className="sg-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="sg-drawer__body">
          <div className="sg-step">
            <div className="sg-step__title">By priority</div>
            <div className="sg-table">
              <div className="sg-tr sg-th sg-tr--prio">
                <span>Priority</span><span>Timing</span><span>Email</span><span>Slack</span>
              </div>
              {PRIORITIES.map(([key, label]) => (
                <div key={key} className="sg-tr sg-tr--prio">
                  <span>{label}</span>
                  <span>
                    <SignalPicker value={form[key].timing} options={TIMINGS} onChange={v => setRule(key, { timing: v })} className="sg-pick--wide" />
                  </span>
                  <span><input type="checkbox" checked={form[key].email} onChange={e => setRule(key, { email: e.target.checked })} /></span>
                  <span><input type="checkbox" checked={form[key].slack} onChange={e => setRule(key, { slack: e.target.checked })} /></span>
                </div>
              ))}
            </div>
          </div>

          {anySlack && (
            <div className="sg-step">
              <div className="sg-step__title">Slack channel</div>
              <SignalPicker
                value={form.slackChannelId}
                options={[{ value: "", label: "The channel my team connected" }].concat(channels.map(c => ({ value: c.id || c.value, label: c.name || c.label || "" })))}
                onChange={v => setForm({ ...form, slackChannelId: v })}
                searchable
                className="sg-pick--wide"
              />
              <span className="sg-hint">
                {agentsData.slackChannelsLoaded && channels.length === 0
                  ? "Slack isn't connected for your team yet, so nothing can be posted."
                  : "Slack posts go to a channel, so teammates in it will see them too."}
              </span>
            </div>
          )}

          <div className="sg-step">
            <div className="sg-step__title">Active hours</div>
            <label className={`sg-check ${form.activeHoursEnabled ? "on" : ""}`}>
              <input type="checkbox" checked={form.activeHoursEnabled} onChange={e => setForm({ ...form, activeHoursEnabled: e.target.checked })} />
              <span><b>Only email / Slack me during these hours</b><small>Anything earlier waits and goes out when the window opens.</small></span>
            </label>
            {form.activeHoursEnabled && (
              <>
                <div className="sg-range sg-range--hours">
                  <SignalPicker value={Number(form.activeStartHour)} options={HOURS.slice(0, 24)} onChange={v => setForm({ ...form, activeStartHour: v })} />
                  <span className="sg-hint sg-range__to">to</span>
                  <SignalPicker value={Number(form.activeEndHour)} options={HOURS.slice(1)} onChange={v => setForm({ ...form, activeEndHour: v })} />
                </div>
                <label className="sg-hint sg-weekdays">
                  <input type="checkbox" checked={form.weekdaysOnly} onChange={e => setForm({ ...form, weekdaysOnly: e.target.checked })} /> Weekdays only
                </label>
                <SignalPicker
                  value={form.timezone}
                  options={zoneOptions(form.timezone).map(z => ({ value: z, label: z }))}
                  onChange={v => setForm({ ...form, timezone: v })}
                  searchable
                  className="sg-pick--wide"
                />
              </>
            )}
          </div>

          {error && <div className="sg-error">{error}</div>}
        </div>

        <div className="sg-drawer__foot">
          <button className="sg-btn sg-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="sg-btn" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default observer(SignalNotificationSettings);
